import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RequirementsFilters = ({
  filters = {},
  onFiltersChange,
  resultCount = 0
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key, value) => {
    onFiltersChange?.({
      ...filters,
      [key]: value
    });
  };

  const handleSkillsChange = (skillsString) => {
    const skills = skillsString.split(',').map(s => s.trim()).filter(s => s.length > 0);
    handleFilterChange('skills', skills);
  };

  const clearFilters = () => {
    onFiltersChange?.({
      search: '',
      status: '',
      priority: '',
      client: '',
      skills: [],
      location: '',
      deadlineFrom: '',
      deadlineTo: ''
    });
    setShowAdvanced(false);
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'partially_filled', label: 'Partially Filled' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  // Mock client options - in real app, this would come from API
  const clientOptions = [
    { value: '', label: 'All Clients' },
    { value: 'techcorp', label: 'TechCorp Solutions Pvt Ltd' },
    { value: 'manufacturing', label: 'Manufacturing Excellence Ltd' },
    { value: 'healthcare', label: 'Healthcare Plus Network' }
  ];

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'skills') return Array.isArray(value) && value.length > 0;
    return value && value !== '';
  });

  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      {/* Basic Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Input
            placeholder="Search requirements..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            iconName="Search"
            iconPosition="left"
          />
        </div>
        
        <div>
          <Select
            value={filters.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
            options={statusOptions}
            placeholder="Filter by status"
          />
        </div>
        
        <div>
          <Select
            value={filters.priority || ''}
            onChange={(value) => handleFilterChange('priority', value)}
            options={priorityOptions}
            placeholder="Filter by priority"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="flex-1"
          >
            Advanced
          </Button>
          
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearFilters}
              iconName="X"
              size="sm"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <>
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Select
                  value={filters.client || ''}
                  onChange={(value) => handleFilterChange('client', value)}
                  options={clientOptions}
                  placeholder="Filter by client"
                />
              </div>
              
              <div>
                <Input
                  placeholder="Location (e.g., Mumbai, Delhi)"
                  value={filters.location || ''}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  iconName="MapPin"
                  iconPosition="left"
                />
              </div>
              
              <div>
                <Input
                  placeholder="Required skills (comma-separated)"
                  value={(filters.skills || []).join(', ')}
                  onChange={(e) => handleSkillsChange(e.target.value)}
                  iconName="Code"
                  iconPosition="left"
                />
              </div>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Deadline From
                </label>
                <Input
                  type="date"
                  value={filters.deadlineFrom || ''}
                  onChange={(e) => handleFilterChange('deadlineFrom', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Deadline To
                </label>
                <Input
                  type="date"
                  value={filters.deadlineTo || ''}
                  onChange={(e) => handleFilterChange('deadlineTo', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="text-sm text-muted-foreground">
          {resultCount === 0 ? (
            'No requirements found'
          ) : (
            `${resultCount} requirement${resultCount !== 1 ? 's' : ''} found`
          )}
          {hasActiveFilters && (
            <span className="ml-1">(filtered)</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {(filters.skills || []).length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Skills:</span>
              {(filters.skills || []).slice(0, 3).map((skill, idx) => (
                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                  {skill}
                </span>
              ))}
              {(filters.skills || []).length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{(filters.skills || []).length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequirementsFilters;